import { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {
    Check as AcceptIcon,
    Close as RejectIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Upload as UploadIcon,
} from '@mui/icons-material';
import {
    downloadSchemaMappingPreferences,
    deleteSchemaMappingPreference,
    getSchemaMappingPreferences,
    saveSchemaMappingPreference,
    uploadSchemaMappingPreferences,
} from '../services/api';

function SchemaMappingPreferencesPage() {
    const [preferences, setPreferences] = useState([]);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [layerFilter, setLayerFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [noticeSeverity, setNoticeSeverity] = useState('success');
    const [transferBusy, setTransferBusy] = useState(false);
    const uploadInputRef = useRef(null);

    const getSourceLabel = (preference) => (
        preference?.source_label
        || preference?.context?.source_label
        || preference?.context?.legacy_report_name
        || preference?.context?.source_file_name
        || preference?.context?.file_name
        || '-'
    );

    const getDisplayStatus = (preference) => {
        const status = String(preference?.display_status || preference?.status || '').trim().toLowerCase();
        if (status === 'overridden') {
            return { key: 'overridden', label: 'Overridden', color: 'success' };
        }
        if (status === 'accepted') {
            return { key: 'accepted', label: 'Accepted', color: 'success' };
        }
        if (status === 'rejected') {
            return { key: 'rejected', label: 'Rejected', color: 'warning' };
        }
        return { key: status || 'unknown', label: preference?.display_status_label || preference?.status || '-', color: 'default' };
    };

    const loadPreferences = async () => {
        try {
            setLoading(true);
            setError('');
            setNotice('');
            const response = await getSchemaMappingPreferences({
                status: statusFilter === 'all' ? undefined : statusFilter,
                layer: layerFilter === 'all' ? undefined : layerFilter,
                search: search || undefined,
                limit: 1000,
            });
            setPreferences(response?.preferences || []);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load schema mapping preferences');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPreferences();
    }, [statusFilter, layerFilter]);

    const handleApplySearch = async () => {
        await loadPreferences();
    };

    const handleUpdateStatus = async (preference, status) => {
        try {
            setSavingId(preference.id);
            setError('');
            setNotice('');
            await saveSchemaMappingPreference({
                legacy_logical: preference.legacy_field?.logical_name || '',
                legacy_physical: preference.legacy_field?.physical_name || '',
                legacy_table: preference.legacy_field?.table_name || '',
                target_logical: preference.target_field?.logical_name || '',
                target_physical: preference.target_field?.physical_name || '',
                target_table: preference.target_field?.table_name || '',
                target_layer: preference.target_field?.layer || '',
                status,
                source: preference.source || 'manual_correction',
                notes: preference.notes || '',
                decided_by: 'user',
                context: preference.context || {},
            });
            await loadPreferences();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update preference');
        } finally {
            setSavingId('');
        }
    };

    const handleDeletePreference = async (preference) => {
        const confirmed = window.confirm(
            `Delete this schema preference permanently?\n\nLegacy: ${preference.legacy_field?.physical_name || '-'}\nTarget: ${preference.target_field?.table_name || '-'}.${preference.target_field?.physical_name || '-'} [${preference.target_field?.layer || '-'}]`
        );
        if (!confirmed) {
            return;
        }

        try {
            setSavingId(preference.id);
            setError('');
            setNotice('');
            await deleteSchemaMappingPreference(preference.id);
            await loadPreferences();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to delete preference');
        } finally {
            setSavingId('');
        }
    };

    const handleDownload = async () => {
        try {
            setTransferBusy(true);
            setError('');
            const { blob, filename } = await downloadSchemaMappingPreferences();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || 'schema_mapping_preferences.json';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setNoticeSeverity('success');
            setNotice('Schema mapping preferences downloaded.');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to download schema mapping preferences');
        } finally {
            setTransferBusy(false);
        }
    };

    const handleUploadClick = () => {
        uploadInputRef.current?.click();
    };

    const handleUploadFile = async (event) => {
        const [file] = Array.from(event.target.files || []);
        event.target.value = '';
        if (!file) {
            return;
        }

        try {
            setTransferBusy(true);
            setError('');
            setNotice('');
            const summary = await uploadSchemaMappingPreferences(file);
            await loadPreferences();
            const errorCount = Array.isArray(summary?.errors) ? summary.errors.length : 0;
            setNoticeSeverity(errorCount ? 'warning' : 'success');
            setNotice(
                `Imported ${summary?.imported || 0} preferences (${summary?.created || 0} created, ${summary?.updated || 0} updated, ${summary?.skipped || 0} skipped).`
                + (errorCount ? ` ${errorCount} record(s) reported errors.` : '')
            );
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to upload schema mapping preferences');
        } finally {
            setTransferBusy(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>Schema Mapping Preferences</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Global schema-mapper decisions reused across projects and legacy files.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <input
                        ref={uploadInputRef}
                        type="file"
                        accept=".json,application/json"
                        hidden
                        onChange={handleUploadFile}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        disabled={loading || transferBusy}
                    >
                        Download
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={handleUploadClick}
                        disabled={loading || transferBusy}
                    >
                        Upload
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadPreferences}
                        disabled={loading || transferBusy}
                    >
                        Refresh
                    </Button>
                </Stack>
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
                <TextField
                    label="Search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            handleApplySearch();
                        }
                    }}
                    fullWidth
                    placeholder="Legacy field, target column, or target table"
                />
                <TextField
                    select
                    label="Status"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    sx={{ minWidth: 180 }}
                >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="accepted">Accepted</MenuItem>
                    <MenuItem value="overridden">Overridden</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                </TextField>
                <TextField
                    select
                    label="Layer"
                    value={layerFilter}
                    onChange={(event) => setLayerFilter(event.target.value)}
                    sx={{ minWidth: 160 }}
                >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="L2">L2</MenuItem>
                    <MenuItem value="L3">L3</MenuItem>
                </TextField>
                <Button variant="contained" onClick={handleApplySearch} disabled={loading}>
                    Search
                </Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {notice && <Alert severity={noticeSeverity} sx={{ mb: 2 }}>{notice}</Alert>}

            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                {loading ? (
                    <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer sx={{ maxHeight: '70vh' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Legacy Source</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Target Mapping</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Layer</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                                    <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {preferences.map((preference) => {
                                    const displayStatus = getDisplayStatus(preference);
                                    return (
                                        <TableRow key={preference.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {[preference.legacy_field?.table_name, preference.legacy_field?.physical_name].filter(Boolean).join('.') || preference.legacy_field?.physical_name || '-'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {preference.legacy_field?.logical_name || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {preference.target_field?.table_name || '-'}.{preference.target_field?.physical_name || '-'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {preference.target_field?.logical_name || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{preference.target_field?.layer || '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={displayStatus.label}
                                                    color={displayStatus.color}
                                                    size="small"
                                                    variant={displayStatus.key === 'unknown' ? 'outlined' : 'filled'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {getSourceLabel(preference)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={0.5} justifyContent="center">
                                                    <Button
                                                        size="small"
                                                        variant={preference.status === 'accepted' ? 'contained' : 'outlined'}
                                                        color="success"
                                                        startIcon={savingId === preference.id ? <CircularProgress size={14} color="inherit" /> : <AcceptIcon />}
                                                        onClick={() => handleUpdateStatus(preference, 'accepted')}
                                                        disabled={savingId === preference.id}
                                                    >
                                                        Accept
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant={preference.status === 'rejected' ? 'contained' : 'outlined'}
                                                        color="warning"
                                                        startIcon={savingId === preference.id ? <CircularProgress size={14} color="inherit" /> : <RejectIcon />}
                                                        onClick={() => handleUpdateStatus(preference, 'rejected')}
                                                        disabled={savingId === preference.id}
                                                    >
                                                        Reject
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        startIcon={savingId === preference.id ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                                                        onClick={() => handleDeletePreference(preference)}
                                                        disabled={savingId === preference.id}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {!preferences.length && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                            <Typography color="text.secondary">No schema mapping preferences found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
}

export default SchemaMappingPreferencesPage;
